import "./InputText.style.scss";

import React, { useEffect, useState } from "react";

import { randomString } from "../../helper/string.helpers";
import Icon from "../Icon/Icon";
import InputWrapper from "../InputWrapper/InputWrapper";

/**
 * @typedef {Object} InputTextProps
 * @property {string | null} [id] - The ID for the input
 * @property {string | null} [className] - Additional Class(s) to add to the Component.
 * @property {string} [type] - The input `type`.
 * @property {string} [label] - The label to appear above the input
 * @property {boolean} [srOnlyLabel] -
 * @property {boolean} [inline] -
 * @property {string} [moreInfo] -
 * @property {string} [footnote] -
 * @property {"md" | "sm"} [size] -
 * @property {"default" | "gray-outline" | null} [theme] -
 * @property {boolean} [required] -
 * @property {boolean} [invalid] -
 * @property {string} [invalidMessage] -
 * @property {string} [prefixIcon] -
 * @property {string} [suffixIcon] -
 * @property {string} [prefix] - Text prefix to display (e.g., "$" for currency)
 * @property {string} [defaultValue] - Default value to use when value is empty
 * @property {string} [placeholder] -
 * @property {boolean} [autocomplete] -
 * @property {boolean} [readonly] -
 * @property {boolean} [disabled] -
 * @property {string | number} value -
 * @property {React.Dispatch<React.SetStateAction<InputTextProps["value"]>>} onValueChange -
 */

/**
 * An InputText Component.
 * @param {InputTextProps} props
 * @return {JSX.Element}
 * @constructor
 */
/** @type InputTextProps */

const InputText = (props) => {
	const {
		onKeyDown = () => false,
		id = randomString(),
		className = "",
		type = "text",
		label = "",
		srOnlyLabel = false,
		inline = false,
		moreInfo = "",
		footnote = "",
		size = "md",
		theme = "default",
		required = false,
		invalid = false,
		invalidMessage = "This is required",
		prefixIcon = "",
		suffixIcon = "",
		prefix = "",
		defaultValue = "",
		placeholder = "",
		autocomplete = false,
		readonly = false,
		disabled = false,
		value = "",
		min = "",
		max = "",
		onValueChange = (v) => v,
		onBlur = () => {},
		onFocus = () => {},
		wrapperClasses = "",
		inputClasses = "",
	} = props;

	const [maxError, setMaxError] = useState(false);

	useEffect(() => {
		if (defaultValue && (!value || value === "")) {
			onValueChange(defaultValue);
		}
	}, [defaultValue, value, onValueChange]);

	useEffect(() => {
		if (max && value) {
			const numericValue = parseFloat(getStoredValue(value));
			const maxValue = parseFloat(max);

			if (!isNaN(numericValue) && !isNaN(maxValue) && numericValue > maxValue) {
				setMaxError(true);
			} else {
				setMaxError(false);
			}
		} else {
			setMaxError(false);
		}
	}, [value, max, prefix]);

	const getClasses = () => {
		const classes = ["input-text"];
		className && classes.push(className);
		theme && classes.push(`theme-${theme}`);
		size && classes.push(`size-${size}`);
		return classes.join(" ");
	};

	const getDisplayValue = () => {
		const currentValue = value || defaultValue || "";

		if (!prefix) return currentValue;

		const stringValue = String(currentValue);

		if (stringValue.startsWith(prefix)) {
			return stringValue;
		}

		if (!stringValue) {
			return "";
		}

		return `${prefix}${stringValue}`;
	};

	const getStoredValue = (inputValue) => {
		if (!prefix) return inputValue;

		const stringValue = String(inputValue);
		if (stringValue.startsWith(prefix)) {
			return stringValue.slice(prefix.length);
		}

		return inputValue;
	};

	const handleChange = (e) => {
		const { value: inputValue } = e.target;
		const storedValue = getStoredValue(inputValue);
		onValueChange(storedValue);
	};

	const isInvalid = invalid || maxError;

	const errorMessage = maxError ? `Must be less than ${max}` : invalidMessage;

	return (
		<InputWrapper
			id={id}
			wrapperClasses={wrapperClasses}
			labelClass={props.labelClass}
			theme={theme}
			label={label}
			srOnlyLabel={srOnlyLabel}
			required={required}
			moreInfo={moreInfo}
			footnote={footnote}
			inline={inline}
			invalid={isInvalid}
			invalidMessage={errorMessage}
		>
			<div className={getClasses()}>
				<input
					type={type}
					id={id}
					className={`${inputClasses} ${isInvalid ? "is-invalid" : ""} ${prefixIcon ? "prefix" : ""} ${
						suffixIcon ? "suffix" : ""
					}`}
					onKeyDown={onKeyDown}
					placeholder={placeholder}
					readOnly={readonly}
					disabled={disabled}
					autoComplete={autocomplete ? "on" : "off"}
					inputMode='text'
					value={getDisplayValue()}
					onChange={handleChange}
					min={min}
					max={max}
					onBlur={onBlur}
					onFocus={onFocus}
				/>

				{prefixIcon && (
					<div className='input-prefix-icon'>
						<Icon icon={prefixIcon} size={size} />
					</div>
				)}

				{suffixIcon && (
					<div className='input-suffix-icon'>
						<Icon icon={suffixIcon} size={size} />
					</div>
				)}
			</div>
		</InputWrapper>
	);
};

export default InputText;
